import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { CanvasContext } from "../../../contexts/CanvasContext";
import {
  faTrash,
  faPlus,
  faBars,
  faCode,
  faLayerGroup,
  faDownload,
  faFile,
  faPlay,
  faScrewdriverWrench,
  faEye,
  faPen,
  faNewspaper,
  faTableCells,
  faDatabase,
  faUndo,
  faRedo,
} from "@fortawesome/free-solid-svg-icons";

import "./Navbar.css";

const Modal = ({ isOpen, children }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">{children}</div>
    </div>
  );
};

const Navbar = ({
  showCode,
  setShowCode,
  setTriggerDownload,
  showLeftSidebar,
  setShowLeftSidebar,
  showPageManager,
  setShowPageManager,
  showPreview,
  setShowPreview,
  showLayers,
  setShowLayers,
  showProperties,
  setShowProperties,
  setShowLandingNavbar,
  showLandingNavbar,
  setShowDocumentation,
  setLayOut,
  LayOut,
}) => {
  const {
    createNewProject,
    handleDeleteProject,
    showGrid,
    setShowGrid,
    showFileManager,
    setShowFileManager,
    reusableComponents,
    deleteReusableComponent,
    queueUndoRedoOperation,
    isUndoRedoInProgress,
    isUndoInProgress,
    isRedoInProgress,
  } = useContext(CanvasContext);

  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const [showUndoSpinner, setShowUndoSpinner] = useState(false);
  const [showRedoSpinner, setShowRedoSpinner] = useState(false);

  useEffect(() => {
    if (isUndoRedoInProgress && isUndoInProgress) {
      // Start the spinner when undo/redo starts
      setShowUndoSpinner(true);
    }
    if (isUndoRedoInProgress && isRedoInProgress) {
      // Start the spinner when undo/redo starts
      setShowRedoSpinner(true);
    } else if (showUndoSpinner || showRedoSpinner) {
      // Stop the spinner after 1 second to ensure it rotates at least once
      const timer = setTimeout(() => {
        setShowUndoSpinner(false);
        setShowRedoSpinner(false);
      }, 500); // 1 second timeout to complete the animation
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUndoRedoInProgress]);

  const handleCreateNewProject = () => {
    setShowNewProjectModal(true);
    setShowLandingNavbar(false);
    setShowViewDropdown(false);
  };
  const handleDeleteProjectModal = () => {
    setShowDeleteProjectModal(true);
    setShowLandingNavbar(false);
    setShowViewDropdown(false);
  };
  const handleDeleteProjectModalClose = () => {
    setShowDeleteProjectModal(false);
  };

  const handleDeleteProjectAndReusableComponents = () => {
    reusableComponents.forEach((component) => {
      deleteReusableComponent(component.id);
    });
    handleDeleteProject();
    setShowDeleteProjectModal(false);
  };

  const handleNewProjectOption = async (option) => {
    setShowNewProjectModal(false);
    if (option === "export") {
      await setTriggerDownload(true);
      createNewProject();
    } else if (option === "new") {
      createNewProject();
    }
  };

  const handleShowLeftsidebar = () => {
    setShowLeftSidebar(!showLeftSidebar);
    setShowPageManager(false);
    setShowLandingNavbar(false);
    setShowViewDropdown(false);
  };

  const handleShowCodeClick = () => {
    setShowCode(!showCode);
    setShowLandingNavbar(false);
    setShowViewDropdown(false);

    if (!showCode) {
      setShowPreview(false);
      setShowDocumentation(false);
      setShowPageManager(false);
    }
  };

  const handleShowPreviewClick = () => {
    setShowPreview(!showPreview);
    setShowLandingNavbar(false);
    setShowViewDropdown(false);

    if (!showPreview) {
      setShowCode(false);
      setShowDocumentation(false);
      setShowPageManager(false);
    }
  };

  const goHome = () => {
    setShowLeftSidebar(true);
    setShowCode(false);
    setShowPreview(false);
    setShowProperties(true);
    setShowLayers(true);
    setShowDocumentation(false);
    setShowLandingNavbar(false);
    setShowViewDropdown(false);

    LayOut === 1 ? setShowPageManager(false) : setShowPageManager(true);
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowViewDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleUndo = useCallback(() => {
    queueUndoRedoOperation("undo");
  }, [queueUndoRedoOperation]);

  const handleRedo = useCallback(() => {
    queueUndoRedoOperation("redo");
  }, [queueUndoRedoOperation]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.code === "KeyZ" &&
        !event.shiftKey
      ) {
        if (!isUndoRedoInProgress && !isUndoInProgress) handleUndo();
      }

      if (
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.code === "KeyZ"
      ) {
        if (!isUndoRedoInProgress && !isRedoInProgress) handleRedo();
      }

      if (event.ctrlKey && event.key === "b") {
        setShowLeftSidebar(!showLeftSidebar);
      }

      if (event.shiftKey && event.altKey && event.key === "p") {
        setShowPageManager(!showPageManager);
      }
      if (event.altKey && event.key === "l") {
        setShowLayers(!showLayers);
      }
      if (event.ctrlKey && event.altKey && event.key === "p") {
        setShowProperties(!showProperties);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    showLeftSidebar,
    setShowLeftSidebar,
    showPageManager,
    setShowPageManager,
    showLayers,
    setShowLayers,
    showProperties,
    setShowProperties,
    handleUndo,
    handleRedo,
    isUndoRedoInProgress,
    isUndoInProgress,
    isRedoInProgress,
  ]);

  const handleDefaultViewClick = (event) => {
    event.stopPropagation();
    setShowViewDropdown(!showViewDropdown);
    setShowLandingNavbar(false);
  };

  const classForNavbar =
    LayOut === 1 || LayOut === 2 || LayOut === 3 ? "navForOne" : "nav";

  return (
    <>
      <nav className={classForNavbar}>
        <button
          className={`button ${showLandingNavbar ? "active" : ""}`}
          onClick={() => {
            setShowLandingNavbar(!showLandingNavbar);
            setShowViewDropdown(false);
          }}
        >
          <FontAwesomeIcon icon={faBars} />
          {/* <span className="label">Menu</span>  */}
        </button>
        <button
          className={`button ${showGrid}`}
          onClick={() => {
            setShowGrid(!showGrid);
            // setShowViewDropdown(false);
          }}
        >
          <FontAwesomeIcon icon={faTableCells} />
          <span className="label">
            {showGrid ? "Hide Grids" : "Show Grids"}
          </span>
        </button>
        <button className="button" onClick={() => goHome()}>
          <FontAwesomeIcon icon={faEye} />
          <span className="label">Default View</span>
        </button>
        <div ref={dropdownRef}>
          <button
            className={`button ${showViewDropdown ? "active" : ""}`}
            onClick={handleDefaultViewClick}
          >
            <FontAwesomeIcon icon={faNewspaper} />
            <span className="label">Layouts</span>
          </button>
          {showViewDropdown && (
            <div className="dropdown-contentForLayOut">
              <button
                className={`button ${LayOut === 0 ? "active" : ""}`}
                onClick={() => {
                  setLayOut(0);
                  setShowViewDropdown(false);
                }}
              >
                Default Layout
              </button>
              <button
                className={`button ${LayOut === 1 ? "active" : ""}`}
                onClick={() => {
                  setLayOut(1);
                  setShowViewDropdown(false);
                }}
              >
                Layout 1
              </button>
              <button
                className={`button ${LayOut === 2 ? "active" : ""}`}
                onClick={() => {
                  setLayOut(2);
                  setShowViewDropdown(false);
                }}
              >
                Layout 2
              </button>
              <button
                className={`button ${LayOut === 3 ? "active" : ""}`}
                onClick={() => {
                  setLayOut(3);
                  setShowViewDropdown(false);
                }}
              >
                Layout 3
              </button>
            </div>
          )}
        </div>

        <React.Fragment>
          {showUndoSpinner ? (
            <span className="button">
              <FontAwesomeIcon
                icon={faUndo}
                className="spinner-anticlockwise"
              />
            </span>
          ) : (
            <button className="button" onClick={handleUndo}>
              <FontAwesomeIcon icon={faUndo} />
              <span className="label">Undo (ctr+z)</span>
            </button>
          )}
        </React.Fragment>

        <React.Fragment>
          {showRedoSpinner ? (
            <span className="button">
              <FontAwesomeIcon icon={faRedo} className="spinner-clockwise" />
            </span>
          ) : (
            <button className="button" onClick={handleRedo}>
              <FontAwesomeIcon icon={faRedo} />
              <span className="label">Redo (ctr+y)</span>
            </button>
          )}
        </React.Fragment>

        <button
          className={`button ${showLeftSidebar ? "active" : ""}`}
          onClick={handleShowLeftsidebar}
        >
          <FontAwesomeIcon icon={faScrewdriverWrench} />
          <span className="label">
            {showLeftSidebar ? "Hide ToolBar(ctrl+B)" : "Show ToolBar (ctrl+B)"}
          </span>
        </button>
        <button
          className={`button ${showLayers ? "active" : ""}`}
          onClick={() => {
            setShowLayers(!showLayers);
            setShowLandingNavbar(false);
            setShowViewDropdown(false);
          }}
        >
          <FontAwesomeIcon icon={faLayerGroup} />
          <span className="label">
            {showLayers ? "Hide Layers(alt+L)" : "Show Layers(alt+L)"}
          </span>
        </button>
        <button
          className={`button ${showProperties ? "active" : ""}`}
          onClick={() => {
            setShowProperties(!showProperties);
            setShowLandingNavbar(false);
            setShowViewDropdown(false);
          }}
        >
          <FontAwesomeIcon icon={faPen} />
          <span className="label">
            {showProperties
              ? "Hide Properties(ctl+alt+p)"
              : "Show Properties(ctl+alt+p)"}
          </span>
        </button>
        <button
          className={`button ${showPageManager ? "active" : ""}`}
          onClick={() => {
            setShowPageManager(!showPageManager);
            setShowLandingNavbar(false);
            setShowViewDropdown(false);
          }}
        >
          <FontAwesomeIcon icon={faFile} />
          <span className="label">
            {showPageManager
              ? "Hide Pages(shift+alt+p)"
              : "Show Pages(shift+alt+p)"}
          </span>
        </button>
        <button
          className={`button ${showFileManager ? "active" : ""}`}
          onClick={() => {
            setShowFileManager(!showFileManager);
            setShowLandingNavbar(false);
            setShowViewDropdown(false);
          }}
        >
          <FontAwesomeIcon icon={faDatabase} />
          <span className="label">
            {showFileManager ? "Hide Data" : "Show Data"}
          </span>
        </button>
        <button
          className={`button ${showCode ? "active" : ""}`}
          onClick={handleShowCodeClick}
        >
          <FontAwesomeIcon icon={faCode} />
          <span className="label">{showCode ? "Hide Code" : "Show Code"}</span>
        </button>
        <button
          className={`button ${showPreview ? "active" : ""}`}
          onClick={handleShowPreviewClick}
        >
          <FontAwesomeIcon icon={faPlay} />
          <span className="label">Preview</span>
        </button>
        <button
          className="button"
          onClick={() => {
            setTriggerDownload(true);
            setShowLandingNavbar(false);
            setShowViewDropdown(false);
          }}
        >
          <FontAwesomeIcon icon={faDownload} />
          <span className="label">Export Project</span>
        </button>
        <button className="button" onClick={handleDeleteProjectModal}>
          <FontAwesomeIcon icon={faTrash} />
          <span className="label">Delete Project</span>
        </button>
        <button className="button" onClick={handleCreateNewProject}>
          <FontAwesomeIcon icon={faPlus} />
          <span className="label">Create New Project</span>
        </button>
      </nav>

      <Modal
        isOpen={showNewProjectModal}
        onClose={() => setShowNewProjectModal(false)}
      >
        <h3>Create New Project</h3>
        <button onClick={() => handleNewProjectOption("export")}>
          Export current project and create new
        </button>
        <button id="cancel" onClick={() => handleNewProjectOption("new")}>
          Create new project without exporting
        </button>
        <button onClick={() => handleNewProjectOption("cancel")}>Cancel</button>
      </Modal>

      <Modal
        isOpen={showDeleteProjectModal}
        onClose={handleDeleteProjectModalClose}
      >
        <h3>Delete Project</h3>
        <p>
          Are you sure you want to delete the project and all reusable
          components? This action cannot be undone.
        </p>
        <button id="cancel" onClick={handleDeleteProjectAndReusableComponents}>
          Confirm
        </button>
        <button onClick={handleDeleteProjectModalClose}>Cancel</button>
      </Modal>
    </>
  );
};

export default Navbar;

// import React, { useContext, useEffect, useRef, useState } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// import { CanvasContext } from "../../../contexts/CanvasContext";
// import {
//   faTrash,
//   faPlus,
//   faBars,
//   faCode,
//   faLayerGroup,
//   faDownload,
//   faFile,
//   faPlay,
//   faScrewdriverWrench,
//   faEye,
//   faPen,
//   faNewspaper,
//   faTableCells,
//   faDatabase,
//   // faUndo,
//   // faRedo,
// } from "@fortawesome/free-solid-svg-icons";

// import "./Navbar.css";

// const Modal = ({ isOpen, children }) => {
//   if (!isOpen) return null;

//   return (
//     <div className="modal-overlay">
//       <div className="modal-content">{children}</div>
//     </div>
//   );
// };

// const Navbar = ({
//   showCode,
//   setShowCode,
//   setTriggerDownload,
//   showLeftSidebar,
//   setShowLeftSidebar,
//   showPageManager,
//   setShowPageManager,
//   showPreview,
//   setShowPreview,
//   showLayers,
//   setShowLayers,
//   showProperties,
//   setShowProperties,
//   setShowLandingNavbar,
//   showLandingNavbar,
//   setShowDocumentation,
//   setLayOut,
//   LayOut,
//   // undo,
//   // redo,
//   // canUndo,
//   // canRedo,
// }) => {
//   const {
//     createNewProject,
//     handleDeleteProject,
//     showGrid,
//     setShowGrid,
//     showFileManager,
//     setShowFileManager,
//     reusableComponents,
//     deleteReusableComponent,
//   } = useContext(CanvasContext);

//   const [showNewProjectModal, setShowNewProjectModal] = useState(false);
//   const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
//   const [showViewDropdown, setShowViewDropdown] = useState(false);
//   const dropdownRef = useRef(null);

//   const handleCreateNewProject = () => {
//     setShowNewProjectModal(true);
//     setShowLandingNavbar(false);
//     setShowViewDropdown(false);
//   };
//   const handleDeleteProjectModal = () => {
//     setShowDeleteProjectModal(true);
//     setShowLandingNavbar(false);
//     setShowViewDropdown(false);
//   };
//   const handleDeleteProjectModalClose = () => {
//     setShowDeleteProjectModal(false);
//   };

//   const handleDeleteProjectAndReusableComponents = () => {
//     reusableComponents.forEach((component) => {
//       deleteReusableComponent(component.id);
//     });
//     handleDeleteProject();
//     setShowDeleteProjectModal(false);
//   };

//   const handleNewProjectOption = async (option) => {
//     setShowNewProjectModal(false);
//     if (option === "export") {
//       await setTriggerDownload(true);
//       createNewProject();
//     } else if (option === "new") {
//       createNewProject();
//     }
//   };

//   const handleShowLeftsidebar = () => {
//     setShowLeftSidebar(!showLeftSidebar);
//     setShowPageManager(false);
//     setShowLandingNavbar(false);
//     setShowViewDropdown(false);
//   };

//   const handleShowCodeClick = () => {
//     setShowCode(!showCode);
//     setShowLandingNavbar(false);
//     setShowViewDropdown(false);

//     if (!showCode) {
//       setShowPreview(false);
//       setShowDocumentation(false);
//       setShowPageManager(false);
//     }
//   };

//   const handleShowPreviewClick = () => {
//     setShowPreview(!showPreview);
//     setShowLandingNavbar(false);
//     setShowViewDropdown(false);

//     if (!showPreview) {
//       setShowCode(false);
//       setShowDocumentation(false);
//       setShowPageManager(false);
//     }
//   };

//   const goHome = () => {
//     setShowLeftSidebar(true);
//     setShowCode(false);
//     setShowPreview(false);
//     setShowProperties(true);
//     setShowLayers(true);
//     setShowDocumentation(false);
//     setShowLandingNavbar(false);
//     setShowViewDropdown(false);

//     LayOut === 1 ? setShowPageManager(false) : setShowPageManager(true);
//   };
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setShowViewDropdown(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   useEffect(() => {
//     const handleKeyDown = (event) => {
//       // if (event.ctrlKey && event.key === "z") {
//       //   undo();
//       // }
//       // if (event.ctrlKey && event.key === "Z") {
//       //   undo();
//       // }
//       // if (event.ctrlKey && event.key === "y") {
//       //   redo();
//       // }
//       // if (event.ctrlKey && event.key === "Y") {
//       //   redo();
//       // }
//       if (event.ctrlKey && event.key === "b") {
//         setShowLeftSidebar(!showLeftSidebar);
//       }
//       if (event.ctrlKey && event.key === "B") {
//         setShowLeftSidebar(!showLeftSidebar);
//       }
//       if (event.shiftKey && event.altKey && event.key === "p") {
//         setShowPageManager(!showPageManager);
//       }
//       if (event.shiftKey && event.altKey && event.key === "P") {
//         setShowPageManager(!showPageManager);
//       }
//       if (event.altKey && event.key === "l") {
//         setShowLayers(!showLayers);
//       }
//       if (event.altKey && event.key === "L") {
//         setShowLayers(!showLayers);
//       }
//       if (event.ctrlKey && event.altKey && event.key === "p") {
//         setShowProperties(!showProperties);
//       }
//       if (event.ctrlKey && event.altKey && event.key === "P") {
//         setShowProperties(!showProperties);
//       }
//     };

//     window.addEventListener("keydown", handleKeyDown);

//     return () => {
//       window.removeEventListener("keydown", handleKeyDown);
//     };
//   }, [
//     showLeftSidebar,
//     setShowLeftSidebar,
//     showPageManager,
//     setShowPageManager,
//     showLayers,
//     setShowLayers,
//     showProperties,
//     // undo,
//     // redo,
//     setShowProperties,
//   ]);

//   const handleDefaultViewClick = (event) => {
//     event.stopPropagation();
//     setShowViewDropdown(!showViewDropdown);
//     setShowLandingNavbar(false);
//   };

//   const classForNavbar =
//     LayOut === 1 || LayOut === 2 || LayOut === 3 ? "navForOne" : "nav";

//   return (
//     <>
//       <nav className={classForNavbar}>
//         <button
//           className={`button ${showLandingNavbar ? "active" : ""}`}
//           onClick={() => {
//             setShowLandingNavbar(!showLandingNavbar);
//             setShowViewDropdown(false);
//           }}
//         >
//           <FontAwesomeIcon icon={faBars} />
//           {/* <span className="label">Menu</span>  */}
//         </button>
//         <button
//           className={`button ${showGrid}`}
//           onClick={() => {
//             setShowGrid(!showGrid);
//             // setShowViewDropdown(false);
//           }}
//         >
//           <FontAwesomeIcon icon={faTableCells} />
//           <span className="label">
//             {showGrid ? "Hide Grids" : "Show Grids"}
//           </span>
//         </button>
//         <button className="button" onClick={() => goHome()}>
//           <FontAwesomeIcon icon={faEye} />
//           <span className="label">Default View</span>
//         </button>
//         <div ref={dropdownRef}>
//           <button
//             className={`button ${showViewDropdown ? "active" : ""}`}
//             onClick={handleDefaultViewClick}
//           >
//             <FontAwesomeIcon icon={faNewspaper} />
//             {/* <span className="label">Layouts</span>  */}
//           </button>
//           {showViewDropdown && (
//             <div className="dropdown-contentForLayOut">
//               <button
//                 className={`button ${LayOut === 0 ? "active" : ""}`}
//                 onClick={() => {
//                   setLayOut(0);
//                   setShowViewDropdown(false);
//                 }}
//               >
//                 Default Layout
//               </button>
//               <button
//                 className={`button ${LayOut === 1 ? "active" : ""}`}
//                 onClick={() => {
//                   setLayOut(1);
//                   setShowViewDropdown(false);
//                 }}
//               >
//                 Layout 1
//               </button>
//               <button
//                 className={`button ${LayOut === 2 ? "active" : ""}`}
//                 onClick={() => {
//                   setLayOut(2);
//                   setShowViewDropdown(false);
//                 }}
//               >
//                 Layout 2
//               </button>
//               <button
//                 className={`button ${LayOut === 3 ? "active" : ""}`}
//                 onClick={() => {
//                   setLayOut(3);
//                   setShowViewDropdown(false);
//                 }}
//               >
//                 Layout 3
//               </button>
//             </div>
//           )}
//         </div>
//         {/* <button className="button" onClick={undo} disabled={!canUndo}>
//           <FontAwesomeIcon icon={faUndo} />
//           <span className="label">Undo (ctr+z)</span>
//         </button>
//         <button className="button" onClick={redo} disabled={!canRedo}>
//           <FontAwesomeIcon icon={faRedo} />
//           <span className="label">Redo (ctr+y)</span>
//         </button> */}
//         <button
//           className={`button ${showLeftSidebar ? "active" : ""}`}
//           onClick={handleShowLeftsidebar}
//         >
//           <FontAwesomeIcon icon={faScrewdriverWrench} />
//           <span className="label">
//             {showLeftSidebar ? "Hide ToolBar(ctrl+B)" : "Show ToolBar (ctrl+B)"}
//           </span>
//         </button>
//         <button
//           className={`button ${showLayers ? "active" : ""}`}
//           onClick={() => {
//             setShowLayers(!showLayers);
//             setShowLandingNavbar(false);
//             setShowViewDropdown(false);
//           }}
//         >
//           <FontAwesomeIcon icon={faLayerGroup} />
//           <span className="label">
//             {showLayers ? "Hide Layers(alt+L)" : "Show Layers(alt+L)"}
//           </span>
//         </button>
//         <button
//           className={`button ${showProperties ? "active" : ""}`}
//           onClick={() => {
//             setShowProperties(!showProperties);
//             setShowLandingNavbar(false);
//             setShowViewDropdown(false);
//           }}
//         >
//           <FontAwesomeIcon icon={faPen} />
//           <span className="label">
//             {showProperties
//               ? "Hide Properties(ctl+alt+p)"
//               : "Show Properties(ctl+alt+p)"}
//           </span>
//         </button>
//         <button
//           className={`button ${showPageManager ? "active" : ""}`}
//           onClick={() => {
//             setShowPageManager(!showPageManager);
//             setShowLandingNavbar(false);
//             setShowViewDropdown(false);
//           }}
//         >
//           <FontAwesomeIcon icon={faFile} />
//           <span className="label">
//             {showPageManager
//               ? "Hide Pages(shift+alt+p)"
//               : "Show Pages(shift+alt+p)"}
//           </span>
//         </button>
//         <button
//           className={`button ${showFileManager ? "active" : ""}`}
//           onClick={() => {
//             setShowFileManager(!showFileManager);
//             setShowLandingNavbar(false);
//             setShowViewDropdown(false);
//           }}
//         >
//           <FontAwesomeIcon icon={faDatabase} />
//           <span className="label">
//             {showFileManager ? "Hide Data" : "Show Data"}
//           </span>
//         </button>
//         <button
//           className={`button ${showCode ? "active" : ""}`}
//           onClick={handleShowCodeClick}
//         >
//           <FontAwesomeIcon icon={faCode} />
//           <span className="label">{showCode ? "Hide Code" : "Show Code"}</span>
//         </button>
//         <button
//           className={`button ${showPreview ? "active" : ""}`}
//           onClick={handleShowPreviewClick}
//         >
//           <FontAwesomeIcon icon={faPlay} />
//           <span className="label">Preview</span>
//         </button>
//         <button
//           className="button"
//           onClick={() => {
//             setTriggerDownload(true);
//             setShowLandingNavbar(false);
//             setShowViewDropdown(false);
//           }}
//         >
//           <FontAwesomeIcon icon={faDownload} />
//           <span className="label">Export Project</span>
//         </button>
//         <button className="button" onClick={handleDeleteProjectModal}>
//           <FontAwesomeIcon icon={faTrash} />
//           <span className="label">Delete Project</span>
//         </button>
//         <button className="button" onClick={handleCreateNewProject}>
//           <FontAwesomeIcon icon={faPlus} />
//           <span className="label">Create New Project</span>
//         </button>
//       </nav>

//       <Modal
//         isOpen={showNewProjectModal}
//         onClose={() => setShowNewProjectModal(false)}
//       >
//         <h3>Create New Project</h3>
//         <button onClick={() => handleNewProjectOption("export")}>
//           Export current project and create new
//         </button>
//         <button id="cancel" onClick={() => handleNewProjectOption("new")}>
//           Create new project without exporting
//         </button>
//         <button onClick={() => handleNewProjectOption("cancel")}>Cancel</button>
//       </Modal>

//       <Modal
//         isOpen={showDeleteProjectModal}
//         onClose={handleDeleteProjectModalClose}
//       >
//         <h3>Delete Project</h3>
//         <p>
//           Are you sure you want to delete the project and all reusable
//           components? This action cannot be undone.
//         </p>
//         <button id="cancel" onClick={handleDeleteProjectAndReusableComponents}>
//           Confirm
//         </button>
//         <button onClick={handleDeleteProjectModalClose}>Cancel</button>
//       </Modal>
//     </>
//   );
// };

// export default Navbar;
